import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

import { IntegrationCapabilitySchema } from '../integration-capability/integration-capability';
import { IntegrationProbeStatusSchema } from '../integration-probe-status/integration-probe-status';

/**
 * Which layer answered a field: the encrypted vault, the catalog's `envVar`
 * fallback, or nothing at all.
 *
 * The admin shows it verbatim, because "working, but only because of env" is a
 * different operational fact from "configured by staff" — an env-only
 * deployment must not read as unconfigured *or* as deliberately set up.
 */
export const INTEGRATION_VALUE_SOURCES = ['vault', 'env', 'missing'] as const;

export const IntegrationValueSourceSchema = z.enum(INTEGRATION_VALUE_SOURCES);

export type IntegrationValueSource = (typeof INTEGRATION_VALUE_SOURCES)[number];

/**
 * One field as the staff portal is allowed to see it (N21).
 *
 * A secret is `last4` and never more; a non-secret carries its plain value. The
 * cross-field refinements are not decoration — this schema is the last place a
 * leaked credential can be stopped before it leaves the process, and a test
 * against it fails loudly if a service ever starts populating the wrong field.
 */
export const IntegrationFieldStatusSchema = z
  .object({
    key: z.string().min(1),
    secret: z.boolean(),
    source: IntegrationValueSourceSchema,
    /** The trailing characters of a **secret**, or null. Never the value itself. */
    last4: z.string().max(4).nullable(),
    /** The plain value of a **non-secret** field, or null. */
    value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
  })
  .strict()
  .superRefine((field, ctx) => {
    if (field.secret && field.value !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['value'],
        message: 'A secret field never carries its value (N21).',
      });
    }

    if (!field.secret && field.last4 !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['last4'],
        message: 'A non-secret field is shown in full — masking it hides nothing.',
      });
    }

    if (field.source === 'missing' && (field.last4 !== null || field.value !== null)) {
      ctx.addIssue({
        code: 'custom',
        path: ['source'],
        message: 'A missing field carries neither a value nor a mask.',
      });
    }
  });

export type IntegrationFieldStatusDto = z.infer<typeof IntegrationFieldStatusSchema>;

/** One provider's configuration state — a row in the capability card (P10). */
export const IntegrationProviderStatusSchema = z
  .object({
    provider: z.string().min(1),
    /** False when the platform declares the provider but has no adapter yet. */
    wired: z.boolean(),
    active: z.boolean(),
    /** Every `required` field resolved — from the vault or from env. */
    configured: z.boolean(),
    fields: z.array(IntegrationFieldStatusSchema),
    updatedByUserId: UuidSchema.nullable(),
    updatedAt: z.string().nullable(),
    /**
     * The last test-connection this provider received, or null.
     *
     * Null means "no result to show", which is almost always "never tested" — a
     * refusal (`not_wired`, `not_configured`) is recorded like any other
     * outcome, so it is never the reason. The one other case is a stored code
     * this contract no longer declares, which the api degrades to null rather
     * than failing the whole screen over, and warns about.
     */
    lastProbe: IntegrationProbeStatusSchema.nullable(),
  })
  .strict();

export type IntegrationProviderStatusDto = z.infer<typeof IntegrationProviderStatusSchema>;

/** One capability and every provider under it — exactly one may be active. */
export const IntegrationCapabilityStatusSchema = z
  .object({
    capability: IntegrationCapabilitySchema,
    activeProvider: z.string().min(1).nullable(),
    providers: z.array(IntegrationProviderStatusSchema),
  })
  .strict()
  .superRefine((capability, ctx) => {
    const active = capability.providers.filter((provider) => provider.active);

    if (active.length > 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['providers'],
        message: 'A capability has at most one active provider.',
      });
    }

    const activeKey = active[0]?.provider ?? null;
    if (activeKey !== capability.activeProvider) {
      ctx.addIssue({
        code: 'custom',
        path: ['activeProvider'],
        message: 'The active provider must be the one flagged active in the list.',
      });
    }
  });

export type IntegrationCapabilityStatusDto = z.infer<typeof IntegrationCapabilityStatusSchema>;

/** The whole vault, as the integrations screen loads it in one request. */
export const IntegrationsOverviewSchema = z
  .object({ capabilities: z.array(IntegrationCapabilityStatusSchema) })
  .strict();

export type IntegrationsOverviewDto = z.infer<typeof IntegrationsOverviewSchema>;
